import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLogin, Notification } from 'react-admin';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import appTheme, { tokens } from '../theme';
import { IS_DEMO } from '../utils/demoData';
import { isFirebaseConfigured } from '../utils/config';

/** Strict E.164: + followed by 1–15 digits, first digit non-zero. */
const E164_PATTERN = /^\+[1-9]\d{1,14}$/;
const isValidPhone = phone => typeof phone === 'string' && E164_PATTERN.test(phone.trim());
const isValidCode = code => typeof code === 'string' && /^\d{6}$/.test(code.trim());

const RESEND_COOLDOWN_SECONDS = 30;

/** Persist login error across react-admin's redirect cycle */
const LOGIN_ERROR_KEY = 'skilz_login_error';
const stashLoginError = msg => { try { sessionStorage.setItem(LOGIN_ERROR_KEY, msg); } catch(e) {} };
const popLoginError = () => { try { const m = sessionStorage.getItem(LOGIN_ERROR_KEY); sessionStorage.removeItem(LOGIN_ERROR_KEY); return m || ''; } catch(e) { return ''; } };

const MESSAGES = {
    invalidPhone: 'Enter a valid phone number in E.164 format (e.g. +971****4567).',
    invalidCode: 'Enter the 6-digit code from your SMS.',
    sendSuccess: 'We sent a 6-digit code to your phone.',
    rateLimited: 'Too many attempts. Please wait before trying again.',
    generic: "We couldn't process that request. Please try again.",
    network: "We couldn't reach the server. Check your connection and try again.",
    firebaseNotConfigured: 'Firebase is not configured. Set REACT_APP_FIREBASE_* environment variables.',
    firebaseMisconfigured: 'Firebase configuration error. Please contact the system administrator to verify the Firebase project setup.',
};

/**
 * Maps a Firebase error code to a safe, user-facing message.
 *
 * Firebase can throw errors that leak API key values, project IDs, or internal
 * configuration details in their `.message`. This function ensures those raw
 * messages never reach the UI. Only the error code is inspected.
 *
 * Exported for direct unit testing.
 */
export const mapFirebaseError = (error) => {
    const code = error && error.code;
    if (!code) return MESSAGES.generic;

    // Invalid key / configuration / project mismatch errors
    // Note: Firebase can append detail suffixes to error codes, e.g.
    // "auth/api-key-not-valid.-please-pass-a-valid-api-key." so we use
    // startsWith for the known invalid-key family.
    if (
        code === 'auth/invalid-api-key' ||
        code.startsWith('auth/api-key-not-valid') ||
        code === 'auth/configuration-not-found' ||
        code === 'auth/project-not-found' ||
        code === 'auth/invalid-project-id' ||
        code === 'auth/admin-restricted-operation' ||
        code === 'auth/app-not-authorized' ||
        code === 'auth/unauthorized-domain' ||
        code === 'auth/operation-not-allowed'
    ) {
        return MESSAGES.firebaseMisconfigured;
    }

    // Phone validation
    if (code === 'auth/invalid-phone-number' || code === 'auth/missing-phone-number') {
        return MESSAGES.invalidPhone;
    }

    // Rate limiting and quota
    if (code === 'auth/too-many-requests' || code === 'auth/quota-exceeded') {
        return MESSAGES.rateLimited;
    }

    // Verification code errors
    if (code === 'auth/invalid-verification-code' || code === 'auth/missing-verification-code') {
        return MESSAGES.invalidCode;
    }

    if (code === 'auth/code-expired') {
        return 'The code has expired. Please request a new one.';
    }

    // Network errors
    if (code === 'auth/network-request-failed') {
        return MESSAGES.network;
    }

    // Catch-all: never expose raw Firebase message
    return MESSAGES.generic;
};

const styles = {
    page: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%',
        background: tokens.canvas,
        padding: 24,
        boxSizing: 'border-box',
    },
    card: {
        width: '100%',
        maxWidth: 380,
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: 12,
        padding: 32,
        boxSizing: 'border-box',
        textAlign: 'center',
    },
    logo: { width: 120, height: 120, objectFit: 'contain', marginBottom: 20, borderRadius: 16 },
    field: { width: '100%', marginBottom: 12 },
    button: { width: '100%', padding: '10px 16px' },
    hint: { marginTop: 16, color: tokens.muted, fontSize: 12 },
    error: {
        margin: '0 0 12px',
        padding: '10px 12px',
        borderRadius: 10,
        background: `${tokens.danger}14`,
        color: tokens.danger,
        fontSize: 12,
        lineHeight: 1.5,
        textAlign: 'left',
    },
    loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '24px 0' },
    demoBanner: {
        background: `${tokens.warning}18`,
        color: tokens.warning,
        padding: '8px 12px',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 16,
    },
    recaptcha: { marginBottom: 12 },
};

const PHONE_STEP = 1;
const CODE_STEP = 2;
const LOADING_STEP = 0;

const Login = ({ theme }) => {
    const [phone, setPhone] = useState('+971');
    const [code, setCode] = useState('');
    const [step, setStep] = useState(PHONE_STEP);
    const [error, setError] = useState(() => {
        // Only show stashed error if it came from an actual login attempt
        const hadAttempt = sessionStorage.getItem('skilz_login_attempt');
        if (!hadAttempt) {
            // Cold load — clear any stale stash
            try { sessionStorage.removeItem(LOGIN_ERROR_KEY); } catch(e) {}
            return '';
        }
        return popLoginError();
    });
    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const login = useLogin();
    const inFlight = useRef(false);
    const confirmationResult = useRef(null);
    const recaptchaVerifier = useRef(null);
    const recaptchaContainer = useRef(null);
    const mounted = useRef(true);

    useEffect(() => () => { mounted.current = false; }, []);

    // Cooldown timer
    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const timer = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    // Setup RecaptchaVerifier (only in real mode)
    const setupRecaptcha = useCallback(() => {
        if (IS_DEMO || !isFirebaseConfigured) return;

        try {
            const { auth } = require('../utils/firebase');
            const { RecaptchaVerifier } = require('firebase/auth');

            if (recaptchaVerifier.current) {
                try { recaptchaVerifier.current.clear(); } catch (e) { /* ignore */ }
            }

            if (!recaptchaContainer.current) return;

            recaptchaVerifier.current = new RecaptchaVerifier(auth, recaptchaContainer.current, {
                size: 'invisible',
                callback: () => { /* reCAPTCHA solved */ },
                'expired-callback': () => {
                    if (mounted.current) setError('reCAPTCHA expired. Please try again.');
                },
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            // Log a fixed message only — never pass raw Firebase/provider
            // error objects to console (they may contain API keys or config).
            // eslint-disable-next-line no-console
            console.warn('[firebase] RecaptchaVerifier setup failed.');
        }
    }, []);

    useEffect(() => {
        if (step === PHONE_STEP) {
            setupRecaptcha();
        }
    }, [step, setupRecaptcha]);

    const handlePhoneSubmit = useCallback(async (event) => {
        if (event) event.preventDefault();
        if (inFlight.current || cooldown > 0) return;

        const trimmedPhone = phone.trim();
        if (!isValidPhone(trimmedPhone)) {
            setError(MESSAGES.invalidPhone);
            return;
        }

        // Demo mode: skip Firebase entirely
        if (IS_DEMO) {
            setStep(CODE_STEP);
            return;
        }

        if (!isFirebaseConfigured) {
            setError(MESSAGES.firebaseNotConfigured);
            return;
        }

        inFlight.current = true;
        setBusy(true);
        setError('');
        setStep(LOADING_STEP);

        try {
            const { auth } = require('../utils/firebase');
            const { signInWithPhoneNumber } = require('firebase/auth');

            if (!recaptchaVerifier.current) {
                setupRecaptcha();
            }

            const result = await signInWithPhoneNumber(auth, trimmedPhone, recaptchaVerifier.current);
            confirmationResult.current = result;

            if (!mounted.current) return;
            setStep(CODE_STEP);
        } catch (firebaseError) {
            if (!mounted.current) return;

            const errorCode = firebaseError && firebaseError.code;
            const message = mapFirebaseError(firebaseError);
            setError(message);
            if (errorCode === 'auth/too-many-requests') {
                setCooldown(RESEND_COOLDOWN_SECONDS);
            } else if (errorCode === 'auth/quota-exceeded') {
                setCooldown(60);
            }
            setStep(PHONE_STEP);
        } finally {
            if (mounted.current) {
                inFlight.current = false;
                setBusy(false);
            }
        }
    }, [cooldown, phone, setupRecaptcha]);

    const handleCodeSubmit = useCallback(async (event) => {
        if (event) event.preventDefault();
        if (inFlight.current || cooldown > 0) return;

        if (!isValidCode(code)) {
            setError(MESSAGES.invalidCode);
            return;
        }

        inFlight.current = true;
        setBusy(true);
        setError('');

        try {
            if (IS_DEMO) {
                // Demo: skip Firebase, call login directly with demo payload
                await login({ idToken: 'demo-token' }, '/');
                if (mounted.current) {
                    inFlight.current = false;
                    setBusy(false);
                }
                return;
            }

            // Verify the SMS code with Firebase
            if (!confirmationResult.current) {
                throw new Error('No verification in progress. Please request a new code.');
            }

            const credential = await confirmationResult.current.confirm(code.trim());
            const idToken = await credential.user.getIdToken();

            // Exchange the Firebase ID token with our backend
            try { sessionStorage.setItem('skilz_login_attempt', '1'); } catch(e) {}
            await login({ idToken }, '/');

            if (mounted.current) {
                inFlight.current = false;
                setBusy(false);
            }
        } catch (loginError) {
            // Distinguish Firebase errors (have a `.code`) from backend/auth errors
            const errorCode = loginError && loginError.code;
            let message;
            if (errorCode) {
                // Firebase error — use safe mapping
                message = mapFirebaseError(loginError);
            } else {
                // Backend auth error — use the message directly (already safe strings from authProvider)
                message = (loginError && loginError.message) || MESSAGES.generic;
            }
            // Stash BEFORE mounted check — react-admin may unmount us during redirect
            stashLoginError(message);

            if (!mounted.current) return;
            inFlight.current = false;
            setBusy(false);
            setError(message);
            if (errorCode === 'auth/too-many-requests') {
                setCooldown(RESEND_COOLDOWN_SECONDS);
            }
        }
    }, [code, cooldown, login]);

    const handleResend = useCallback(() => {
        if (cooldown > 0 || inFlight.current) return;
        setCooldown(RESEND_COOLDOWN_SECONDS);
        setCode('');
        setError('');
        confirmationResult.current = null;
        setStep(PHONE_STEP);
    }, [cooldown]);

    const handleChangePhone = useCallback(() => {
        setStep(PHONE_STEP);
        setCode('');
        setError('');
        confirmationResult.current = null;
    }, []);

    const disabled = busy || cooldown > 0;

    return (
        <ThemeProvider theme={createMuiTheme(theme || appTheme)}>
            <div className="sk-login-page" style={styles.page}>
                <div className="sk-login-card" style={styles.card}>
                    <img src="logo.png" alt="Skilz" style={styles.logo} />

                    {IS_DEMO && (
                        <div style={styles.demoBanner}>
                            🔒 Demo Mode — no real SMS will be sent
                        </div>
                    )}

                    {step === LOADING_STEP && (
                        <div style={styles.loading}>
                            <CircularProgress size={28} />
                            <Typography variant="body2" style={{ color: tokens.muted }}>
                                Sending your code…
                            </Typography>
                        </div>
                    )}

                    {error !== '' && step !== LOADING_STEP && (
                        <div style={styles.error} role="alert">{error}</div>
                    )}

                    {step === PHONE_STEP && (
                        <form onSubmit={handlePhoneSubmit} noValidate>
                            <Typography variant="h3" gutterBottom>
                                Welcome to Skilz Admin
                            </Typography>
                            <Typography variant="body2" style={{ color: tokens.muted, marginBottom: 24 }}>
                                Enter your phone number to receive a login code.
                            </Typography>
                            <TextField
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                style={styles.field}
                                label="Phone number"
                                placeholder="+971 50 000 0000"
                                type="tel"
                                variant="outlined"
                                size="small"
                                autoFocus
                                required
                                inputProps={{
                                    autoComplete: 'tel',
                                    'aria-invalid': error ? 'true' : 'false',
                                }}
                            />
                            <Button
                                type="submit"
                                style={styles.button}
                                variant="contained"
                                color="primary"
                                disabled={disabled}
                                aria-busy={busy ? 'true' : 'false'}
                                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {cooldown > 0 ? `Continue (${cooldown}s)` : 'Continue'}
                            </Button>
                        </form>
                    )}

                    {step === CODE_STEP && (
                        <form onSubmit={handleCodeSubmit} noValidate>
                            <Typography variant="h3" gutterBottom>
                                Enter your code
                            </Typography>
                            <Typography variant="body2" style={{ color: tokens.muted, marginBottom: 24 }}>
                                We sent a 6-digit code to {phone}.
                            </Typography>
                            <TextField
                                value={code}
                                onChange={e => setCode(e.target.value)}
                                style={styles.field}
                                label="6-digit code"
                                placeholder="000000"
                                variant="outlined"
                                size="small"
                                autoFocus
                                required
                                inputProps={{
                                    autoComplete: 'one-time-code',
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    maxLength: 6,
                                    'aria-invalid': error ? 'true' : 'false',
                                }}
                            />
                            <Button
                                type="submit"
                                style={styles.button}
                                variant="contained"
                                color="primary"
                                disabled={disabled}
                                aria-busy={busy ? 'true' : 'false'}
                                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {cooldown > 0 ? `Log in (${cooldown}s)` : 'Log in'}
                            </Button>
                            <div style={styles.hint}>
                                Didn't get it?{' '}
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleResend}
                                    onKeyPress={handleResend}
                                    style={{
                                        color: cooldown > 0 ? tokens.muted : tokens.primary,
                                        cursor: cooldown > 0 ? 'default' : 'pointer',
                                        fontWeight: 600,
                                    }}
                                >
                                    {cooldown > 0 ? `Resend (${cooldown}s)` : 'Resend code'}
                                </span>
                                {' · '}
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleChangePhone}
                                    onKeyPress={handleChangePhone}
                                    style={{ color: tokens.primary, cursor: 'pointer', fontWeight: 600 }}
                                >
                                    Use a different number
                                </span>
                            </div>
                        </form>
                    )}
                </div>
                {/* reCAPTCHA container must stay mounted across step changes;
                    it's invisible but Firebase needs the DOM node alive during
                    the async signInWithPhoneNumber call. */}
                <div ref={recaptchaContainer} id="recaptcha-container" />
                <Notification />
            </div>
        </ThemeProvider>
    );
};

export default Login;
